import { useAuth0, authCapabilities } from "ct-auth"
import { Box, Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { getLastKnownOrgId, normalizeRedirectURLForBasename } from "../../../data-utils/authUtils.js"
import { appVersionString } from "../../../data-utils/uiUtils.js"
import logger from "../../../Logger/index.js"
import { DEBUG_FEATURES, DEFAULT_LOGO, ROUTE_PATHS } from "../../../utilities/constants.js"
import Loader from "../../common/Loader/loader.jsx"
import { UiCtButton } from "../../common/MuiStyled/LibComponent.jsx"
import EmailCodeSignIn from "./EmailCodeSignIn.jsx"
import "./Login.scss"

const pageLogger = logger.child({ module: "user login auth0" })

function LoginAuth0() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
  const navigate = useNavigate()

  // Which sign-in affordances this environment actually supports. On Keycloak the
  // IdP's own passwordless connection does not exist, so that button would 400 —
  // the server-minted email-code flow takes its place.
  const capabilities = authCapabilities()
  // `?emailCode=1&email=` is where the Keycloak sign-in page sends an email-code player
  // (bootstrap ensureEmailCodeRouting): open the email-code form, address pre-filled.
  const [handoff] = useState(() => new URLSearchParams(window.location.search))
  const [showEmailCode, setShowEmailCode] = useState(handoff.get("emailCode") === "1")

  useEffect(() => {
    // Skip navigation if we're handling an Auth0 callback — onRedirectCallback owns the redirect in that case
    if (isAuthenticated && !new URLSearchParams(window.location.search).has("code")) {
      navigate(ROUTE_PATHS.GAME_ROOT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // App versioning
  const APP_VERSION = `${window.__RUNTIME_CONFIG__.REACT_APP_VERSION ?? ""}`
  const appVersion = appVersionString(APP_VERSION)

  const handleLogin = async () => {
    let returnTo = normalizeRedirectURLForBasename(window.location.pathname)

    // Redirect to the Staging game (for now) rather than '/'
    if (returnTo === "/") {
      returnTo = ROUTE_PATHS.GAME_ROOT
    }
    pageLogger.info("Logging in...")
    pageLogger.info(`Will return to: ${returnTo}...`)

    const loginOpts = {
      appState: { returnTo },
    }

    // Pass Auth0 Organization context when known (enables SSO/MFA per-org)
    const lastOrg = getLastKnownOrgId()
    if (lastOrg) {
      loginOpts.authorizationParams = { organization: lastOrg }
      pageLogger.info(`Using org context: ${lastOrg}`)
    }

    await loginWithRedirect(loginOpts)
  }

  const handlePasswordlessLogin = async () => {
    let returnTo = normalizeRedirectURLForBasename(window.location.pathname)

    // Redirect to the game root path (for now) rather than '/'
    if (returnTo === "/") {
      returnTo = ROUTE_PATHS.GAME_ROOT
    }
    pageLogger.info("Logging in (passwordless)...")
    pageLogger.info(`Will return to: ${returnTo}...`)

    const authParams = { connection: "email" }
    const lastOrg = getLastKnownOrgId()
    if (lastOrg) {
      authParams.organization = lastOrg
      pageLogger.info(`Using org context: ${lastOrg}`)
    }

    await loginWithRedirect({
      authorizationParams: authParams,
      appState: { returnTo },
    })
  }

  const handlePasswordlessSignUp = async () => {
    let returnTo = normalizeRedirectURLForBasename(window.location.pathname)

    // Redirect to the game root path (for now) rather than '/'
    if (returnTo === "/") {
      returnTo = ROUTE_PATHS.GAME_ROOT
    }
    pageLogger.info("Signing up (passwordless)...")
    pageLogger.info(`Will return to: ${returnTo}...`)

    const authParams = { connection: "email", screen_hint: "signup" }
    const lastOrg = getLastKnownOrgId()
    if (lastOrg) {
      authParams.organization = lastOrg
      pageLogger.info(`Using org context: ${lastOrg}`)
    }

    await loginWithRedirect({
      authorizationParams: authParams,
      appState: { returnTo },
    })
  }

  return isLoading ? (
    <Loader enabled={true} />
  ) : (
    <Stack className="section-login" sx={{ alignItems: "center", justifyContent: "center" }}>
      <div className="login-container">
        <div className="login-brand-wrapper">
          <Stack
            className="brand-container"
            sx={{
              px: 1,
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <img className="brand-logo" src={DEFAULT_LOGO} alt="brand logo" />
            <span className="icon-wrapper py-3">
              <i className="fa-solid fa-user-secret"></i>
            </span>
            <Typography
              variant="h1"
              sx={{
                color: "text.primary",
                padding: (theme) => theme.spacing(1, 2),
                fontSize: { sm: 20, md: 20, lg: 24 },
                fontWeight: 500,
              }}
            >
              Prepare, Respond, Prevent
            </Typography>
            {/* <p className="subtitle">
              With ChaosTrack, you get ripped-from-the-headlines cybersecurity tabletop exercises that take minutes, not days. Your whole team can participate without taking them away from their day job.
            </p> */}
            <Typography variant="subtitle1" sx={{ color: "text.tertiary", fontSize: 14 }}>
              {appVersion ?? ""}
            </Typography>
          </Stack>
        </div>
        <Box
          className="login-form-wrapper"
          sx={{
            px: 1,
            backgroundColor: "background.level2",
          }}
        >
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            {/* NOT a <form>: the email-code panel renders its own form inside this
                container, and nested <form> elements are invalid HTML — the browser
                drops the inner one, so its onSubmit never fires and the submit button
                navigates this outer form instead. This wrapper only ever held buttons
                with onClick handlers, so it never needed form semantics. */}
            <Box className="c-font-14" sx={{ py: 3 }}>
              <Stack
                className="action-wrapper flex-center flex-column"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 3,
                  "& .password-link": {
                    color: "primary.main",
                    cursor: "pointer",
                    fontSize: 14,
                    "&:hover": { color: "primary.plainColor" },
                    "&:active": { color: "primary.plainColor" },
                  },
                }}
              >
                {showEmailCode ? (
                  <EmailCodeSignIn onCancel={() => setShowEmailCode(false)} initialEmail={handoff.get("email") ?? ""} />
                ) : (
                  <>
                    <UiCtButton
                      variant="contained"
                      onClick={handleLogin}
                      sx={{
                        py: 1,
                        width: 120,
                        fontWeight: 500,
                      }}
                    >
                      Login
                    </UiCtButton>
                    {/* Players who lost their invite link. On Auth0 this hands off to the
                        IdP's passwordless connection; elsewhere it opens the server-minted
                        email-code form, which looks the same to the user. */}
                    <div className="password-link" onClick={capabilities.idpPasswordless ? handlePasswordlessLogin : () => setShowEmailCode(true)}>
                      Invitation Expired? Click here to get access.
                    </div>
                    {!capabilities.idpPasswordless && capabilities.serverEmailOtp ? null : (
                      <div className="password-link" onClick={() => setShowEmailCode(true)}>
                        Sign in with an email code instead.
                      </div>
                    )}
                    {DEBUG_FEATURES && capabilities.idpPasswordless ? (
                      <div className="password-link" onClick={handlePasswordlessSignUp}>
                        Looking for a Free Trial? Sign up here.
                      </div>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </div>
    </Stack>
  )
}

export default LoginAuth0
